const CONTROL_STATES = new Set([
  "AI_ENABLED",
  "WAITING_HUMAN",
  "HUMAN_ACTIVE",
  "CLOSED",
]);

const textId = (value) =>
  typeof value === "string" && value.trim() ? value.trim() : "";

const legacyUserId = (value) => {
  if (typeof value === "string") return value.trim();
  if (typeof value === "bigint") return value.toString();
  return Number.isSafeInteger(value) ? String(value) : "";
};

const messageSequence = (message) =>
  Number.isSafeInteger(message?.sequence) && message.sequence > 0
    ? message.sequence
    : Number.MAX_SAFE_INTEGER;

/**
 * 校验旧请求封装返回的AjaxResult，避免业务失败仍被页面当成成功。
 * request.js会解析HTTP 200响应，因此正式客服操作必须继续检查业务code。
 */
export function requireAgentServiceSuccess(response) {
  if (!response || response.code !== 200) {
    const error = new Error(response?.msg || "订单客服操作失败");
    error.code = response?.code;
    throw error;
  }
  return response.data;
}

/**
 * 合并REST历史、WebSocket触发的补读结果和本地发送中消息。
 * 业务消息按businessMessageId唯一，本地消息在服务端确认后按clientMessageId替换。
 */
export function mergeAgentServiceMessages(current = [], incoming = []) {
  const confirmedClientIds = new Set(
    incoming
      .filter((message) => message?.localStatus !== "SENDING")
      .map((message) => textId(message?.clientMessageId))
      .filter(Boolean)
  );
  const merged = new Map();

  [...current, ...incoming].forEach((message) => {
    if (!message || typeof message !== "object") return;
    const clientMessageId = textId(message.clientMessageId);
    if (
      message.localStatus === "SENDING" &&
      clientMessageId &&
      confirmedClientIds.has(clientMessageId)
    ) {
      return;
    }
    const businessMessageId = textId(message.businessMessageId);
    const key = businessMessageId || `client:${clientMessageId}`;
    if (!key || key === "client:") return;
    merged.set(key, { ...message });
  });

  return [...merged.values()].sort((left, right) => {
    const sequenceDifference = messageSequence(left) - messageSequence(right);
    if (sequenceDifference !== 0) return sequenceDifference;
    return String(left.createdAt || "").localeCompare(
      String(right.createdAt || "")
    );
  });
}

/** 返回消息集合内最大的服务端sequence，本地发送中消息不参与补读游标。 */
export function lastAgentServiceSequence(messages = []) {
  return messages.reduce((maximum, message) => {
    const sequence = messageSequence(message);
    return sequence === Number.MAX_SAFE_INTEGER
      ? maximum
      : Math.max(maximum, sequence);
  }, 0);
}

/** 从旧商城两份用户缓存中解析当前用户ID，并统一转为字符串。 */
export function resolveAgentServiceCurrentUserId(userState = {}) {
  const details = userState.userDetails || {};
  const info = userState.userInfo || {};
  const candidates = [
    details.user_id,
    details.userId,
    details.id,
    info.user_id,
    info.userId,
    info.id,
  ];
  return candidates.map(legacyUserId).find(Boolean) || "";
}

/**
 * 根据服务端会话参与者和控制状态生成页面按钮权限。
 * 页面只负责显隐和禁用，Spring仍是最终授权边界。
 */
export function resolveAgentServiceControlPolicy(
  conversation,
  currentUserId,
  viewerRole = ''
) {
  const userId = textId(currentUserId);
  const controlState = conversation?.controlState;
  const reception = viewerRole === 'RECEPTION' && conversation?.scene?.sceneType === 'GENERAL'
    && userId === textId(conversation?.assignedStaffUserId);
  const isCustomer = !reception && userId === textId(conversation?.customerUserId);
  const isStaff = !isCustomer && userId === textId(conversation?.assignedStaffUserId);
  const participantType = isCustomer
    ? "CUSTOMER"
    : isStaff
    ? "BUSINESS_STAFF"
    : "NONE";

  if (!CONTROL_STATES.has(controlState) || participantType === "NONE") {
    return {
      participantType,
      canSend: false,
      canRequestHuman: false,
      canAcceptHuman: false,
      canRejectHandoff: false,
      canReleaseToAi: false,
      canSelectAgent: false,
      canSelectHuman: false,
      statusText: "会话不可用",
    };
  }

  const labels = {
    AI_ENABLED: "AI助手在线",
    WAITING_HUMAN:
      conversation.scene?.sceneType === "GENERAL"
        ? "等待人工客服接管，AI继续回复"
        : "等待业务员接管，AI继续回复",
    HUMAN_ACTIVE:
      conversation.scene?.sceneType === "GENERAL"
        ? "人工客服已接管"
        : "业务员已接管",
    CLOSED: "会话已关闭",
  };
  return {
    participantType,
    canSend:
      controlState !== "CLOSED" &&
      (isCustomer || (isStaff && controlState === "HUMAN_ACTIVE")),
    canRequestHuman: isCustomer && controlState === "AI_ENABLED",
    canAcceptHuman: isStaff && controlState === "WAITING_HUMAN",
    canRejectHandoff: isStaff && controlState === "WAITING_HUMAN",
    canReleaseToAi: isStaff && controlState === "HUMAN_ACTIVE",
    canSelectAgent:
      isCustomer && controlState !== "AI_ENABLED" && controlState !== "CLOSED",
    canSelectHuman: isCustomer && controlState === "AI_ENABLED",
    statusText: labels[controlState],
  };
}

/**
 * 以最新权威发言和控制状态推导AI是否仍在生成回复。
 * WebSocket processing 仅在尚无可判定业务消息时作为兼容提示。
 */
export function deriveAgentServiceAgentProcessing(
  conversation,
  messages = [],
  realtimeProcessing = false,
  cancelledThroughSequence = 0
) {
  if (conversation?.controlState !== "AI_ENABLED") {
    return false;
  }
  const latest = messages
    .filter(
      (message) =>
        message?.actorType !== "SYSTEM" && message?.localStatus !== "FAILED"
    )
    .slice()
    .sort((left, right) => {
      const sequenceDifference = messageSequence(left) - messageSequence(right);
      if (sequenceDifference !== 0) return sequenceDifference;
      return String(left.createdAt || "").localeCompare(
        String(right.createdAt || "")
      );
    })
    .pop();

  if (latest?.actorType === "CUSTOMER") {
    if (cancelledThroughSequence > 0 && messageSequence(latest) <= cancelledThroughSequence) return false;
    return true;
  }
  if (["AI_AGENT", "BUSINESS_STAFF"].includes(latest?.actorType)) return false;
  return Boolean(realtimeProcessing);
}

/**
 * 将正式客服会话转换为旧消息中心可并列展示的条目。
 * 未读数只根据当前参与者自己的服务端游标计算，不读取旧聊天表。
 */
export function mapAgentServiceInboxConversation(
  conversation,
  currentUserId,
  activeConversationId = "",
  viewerRole = ''
) {
  const userId = textId(currentUserId);
  const reception = viewerRole === 'RECEPTION' && conversation?.scene?.sceneType === 'GENERAL'
    && userId === textId(conversation?.assignedStaffUserId);
  const isCustomer = !reception && userId === textId(conversation?.customerUserId);
  const isStaff = !isCustomer && userId === textId(conversation?.assignedStaffUserId);
  const conversationId = textId(conversation?.conversationId);
  const readSequence = isStaff
    ? conversation?.staffLastReadSequence
    : conversation?.customerLastReadSequence;
  const lastSequence = Number.isSafeInteger(conversation?.lastSequence)
    ? conversation.lastSequence
    : 0;
  const normalizedReadSequence = Number.isSafeInteger(readSequence)
    ? readSequence
    : 0;
  const scene = conversation?.scene || {};
  const isGeneral = scene.sceneType === "GENERAL";
  const sceneLabel = isGeneral
    ? "XSOU客服"
    : scene.partNumber || scene.orderNum || "订单客服";
  const isActive = conversationId === textId(activeConversationId);
  const unread = isActive
    ? 0
    : Math.max(0, lastSequence - normalizedReadSequence);
  const aiEnabled = conversation?.controlState === "AI_ENABLED";

  return {
    kind: "CUSTOMER_SERVICE",
    key: `agent-service:${conversationId}${reception && userId === textId(conversation?.customerUserId) ? ':reception' : ''}`,
    conversationId,
    sceneType: scene.sceneType,
    name: isStaff
      ? conversation?.customerDisplayName || "客户"
      : isGeneral
      ? "XSOU客服"
      : conversation?.assignedStaffDisplayName || "业务员",
    lastMessage: sceneLabel,
    unread,
    isUnread: unread > 0,
    // 会话状态、AI开关和已读游标都会更新updatedAt；列表排序只认真实消息时间。
    lastTime: conversation?.lastMessageAt || conversation?.createdAt || "",
    isAssignedStaff: isStaff,
    // 供需单次快照不启用商品/总客服的长期记忆删除入口，避免旧Run与新实例冲突。
    canDelete: isCustomer && scene.sceneType !== 'SUPPLY_DEMAND',
    aiEnabled,
    // 平台总客服不归接待人的个人AI开关管理；接待人只处理人工请求。
    canToggleAi: isStaff && !isGeneral && conversation?.controlState !== "CLOSED",
  };
}

/**
 * 把正式WebSocket通知归纳为页面动作。
 * 通知不带消息正文，收到消息事件后必须通过REST读取MySQL权威消息。
 */
export function reduceAgentServiceNotification(notification = {}) {
  switch (notification.eventType) {
    case "customer.message.created.v1":
      return { refreshMessages: true };
    case "customer.conversation.control.changed.v1":
      return {
        refreshConversation: true,
        controlState: notification.controlState,
        controlVersion: notification.controlVersion,
      };
    case "customer.handoff.requested.v1":
      return { refreshConversation: true };
    case "customer.agent.processing.v1":
      return {
        agentProcessing: true,
        agentRunId: notification.agentRunId,
      };
    case "customer.agent.failed.v1":
      return {
        agentProcessing: false,
        agentRunId: notification.agentRunId,
        agentError: notification.error,
      };
    default:
      return {};
  }
}

/** 根据当前网页协议生成同源的订单客服原生WebSocket地址。 */
export function resolveAgentServiceRealtimeUrl(locationLike) {
  const protocol = locationLike?.protocol === "https:" ? "wss:" : "ws:";
  const host = textId(locationLike?.host);
  if (!host) throw new TypeError("实时连接地址缺少host");
  return `${protocol}//${host}/dev-api/api/mts-api/agent-service/v1/realtime`;
}

/** 生成不包含正文或用户身份的浏览器消息幂等ID。 */
export function createAgentServiceClientMessageId(options = {}) {
  const now = Number.isFinite(options.now) ? options.now : Date.now();
  const random =
    typeof options.random === "function" ? options.random() : Math.random();
  const randomPart = Math.floor(random * 0x100000000)
    .toString(36)
    .padStart(7, "0");
  return `cs-web-${Math.floor(now).toString(36)}-${randomPart}`;
}
